// Skill Tree Types
export interface SkillNode {
  id: number;
  name: string;
  description: string;
  skillCategory: string;
  requiredLevel: number;
  talentPointCost: number;
  prerequisiteSkillId?: number;
  treeTier: number;
  treePosition: number;
  skillEffects: string;
  unlockedSpellId?: string;
  cooldown?: number;
  apCost?: number;
  manaCost?: number;
  iconName?: string;
  isActive: boolean;
  isLearned: boolean;
  canLearn: boolean;
  cannotLearnReason?: string;
}

export interface EquippedSkill {
  hotbarSlot: number;
  skill: SkillNode;
}

export interface LearnedSkill {
  id: number;
  skillNodeId: number;
  skillName: string;
  skillRank: number;
  timesUsed: number;
  learnedAt: string;
  remainingCooldown: number;
  skillLevel: number;
  skillXP: number;
  xpToNextLevel: number;
}

export interface TalentPointBalance {
  available: number;
  totalEarned: number;
  totalSpent: number;
}

export interface StatBonuses {
  vitality: number;
  intelligence: number;
  strength: number;
  dexterity: number;
  constitution: number;
  wisdom: number;
}

export interface CombatBonuses {
  criticalHitChance: number;
  criticalHitDamage: number;
  skillDamageBonus: number;
  healingBonus: number;
  accuracyBonus: number;
  evasionBonus: number;
  experienceBonus: number;
  attackDamageBonus: number;
  flatDamageReduction: number;
  blockChance: number;
}

export interface DamageResistance {
  damageType: string;
  resistancePercent: number;
}

export interface PassiveBonuses {
  statBonuses: StatBonuses;
  combatBonuses: CombatBonuses;
  damageResistances: DamageResistance[];
  passiveAbilities: string[];
}

export interface SkillTreeData {
  characterId: number;
  characterClass: string;
  characterLevel: number;
  availableSkills: SkillNode[];
  learnedSkills: LearnedSkill[];
  talentPoints: TalentPointBalance;
  passiveBonuses: PassiveBonuses;
}

export interface LearnSkillResponse {
  success: boolean;
  learnedSkill?: LearnedSkill;
  talentPointsSpent: number;
  remainingTalentPoints: number;
}

export interface TalentPointTransaction {
  id: number;
  pointChange: number;
  source: string;
  description?: string;
  relatedSkillName?: string;
  createdAt: string;
}

export interface PassiveAbilityDefinition {
  id: string;
  name: string;
  description: string;
  triggerCondition: string;
  effectType: string;
}

// Passive abilities are stored as machine ids (e.g. "vampiric"); look up their catalog entry
// by id, tolerating legacy display-name entries.
export const findPassiveDefinition = (
  definitions: PassiveAbilityDefinition[],
  ability: string,
): PassiveAbilityDefinition | undefined =>
  definitions.find(
    def => def.id?.toLowerCase() === ability.toLowerCase() || def.name.toLowerCase() === ability.toLowerCase(),
  );

// "CriticalHitChance" / "criticalHitChance" -> "Critical Hit Chance"
export const humanizeEffectKey = (key: string): string =>
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

// Combat bonuses are percentages except flat values like FlatDamageReduction
export const formatBonusValue = (key: string, value: number): string =>
  key.toLowerCase().includes('flat') ? `+${value}` : `+${value}%`;

export interface CharacterFullStats {
  characterId: number;
  baseStats: StatBonuses;
  passiveBonuses: StatBonuses;
  finalStats: StatBonuses;
  derivedStats: {
    maxHP: number;
    currentHP: number;
    actionPoints: number;
    movementPoints: number;
    criticalHitChance: number;
    criticalHitDamage: number;
    accuracy: number;
    evasion: number;
  };
  combatBonuses: CombatBonuses;
  damageResistances: DamageResistance[];
  passiveAbilities: string[];
}

// SkillNode.skillEffects is raw JSON authored by the seeder — keys are PascalCase
export interface SkillEffectData {
  StatBonuses?: Record<string, number>;
  CombatBonuses?: Record<string, number>;
  DamageResistances?: { DamageType: string; ResistancePercent: number }[];
  PassiveAbilities?: string[];
  SpecialEffects?: Record<string, unknown>;
}

export interface EvolutionEligibility {
  evolutionId: number;
  baseClass: string;
  advancedClass: string;
  description: string;
  requiredLevel: number;
  requiredTalentPoints: number;
  requiredSkills: string[];
  missingSkills: string[];
  requiredStats: Record<string, number>;
  missingStats: Record<string, number>;
  abilities: string[];
  statBonuses: Record<string, number>;
  alreadyEvolved: boolean;
  isEligible: boolean;
  unmetReasons: string[];
}

export interface EvolveResponse {
  success: boolean;
  newClassTitle: string;
  statBonusesApplied: Record<string, number>;
}